import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { DashboardSectionHeader } from "src/components/common";
import RestaurantAiExecutiveSummary from "../RestaurantAiExecutiveSummary";
import RestaurantHeaderActions, { type RestaurantHeaderActionsProps } from "../RestaurantHeaderActions";
import RestaurantKpiCard from "../RestaurantKpiCard";
import RestaurantRevenueOrdersTrend from "../charts/RestaurantRevenueOrdersTrend";
import RestaurantDonutBreakdown from "../charts/RestaurantDonutBreakdown";
import RestaurantCategoryBars from "../charts/RestaurantCategoryBars";
import RestaurantOrderDensityHeatmap from "../charts/RestaurantOrderDensityHeatmap";
import RestaurantHorizontalBars from "../charts/RestaurantHorizontalBars";
import { BRANCHES } from "../../data/restaurantDemoData";
import { useRestaurantFormatters } from "../../utils/restaurantFormatters";
import type { RestaurantFilters, RestaurantMetrics } from "../../utils/restaurantMetrics";

const ORDER_TYPE_LABEL_KEYS: Record<string, string> = {
  dine_in: "restaurantAnalytics.orderTypes.dineIn",
  takeaway: "restaurantAnalytics.orderTypes.takeaway",
  delivery: "restaurantAnalytics.orderTypes.delivery",
  drive_thru: "restaurantAnalytics.orderTypes.driveThru",
  pickup: "restaurantAnalytics.orderTypes.pickup",
};

const DAYPART_LABEL_KEYS: Record<string, string> = {
  breakfast: "restaurantAnalytics.dayparts.breakfast",
  lunch: "restaurantAnalytics.dayparts.lunch",
  afternoon: "restaurantAnalytics.dayparts.afternoon",
  dinner: "restaurantAnalytics.dayparts.dinner",
  late_night: "restaurantAnalytics.dayparts.lateNight",
};

interface RestaurantOverviewTabProps {
  metrics: RestaurantMetrics;
  filters: RestaurantFilters;
  headerProps: RestaurantHeaderActionsProps;
}

const RestaurantOverviewTab: FC<RestaurantOverviewTabProps> = ({ metrics, filters, headerProps }) => {
  const { t, i18n } = useTranslation();
  const fmt = useRestaurantFormatters();
  const isRTL = i18n.language.toLowerCase().startsWith("ar");

  const orderTypeDonutData = metrics.orderTypes.map(type => ({
    id: type.id,
    label: t(ORDER_TYPE_LABEL_KEYS[type.id] ?? type.id, { defaultValue: type.id }),
    orders: type.orders,
    revenue: type.revenue,
    share: type.share,
  }));

  const daypartRevenueBars = metrics.dayparts.map(part => ({
    id: part.id,
    label: t(DAYPART_LABEL_KEYS[part.id] ?? part.id, { defaultValue: part.id }),
    value: part.revenue,
  }));

  const topBranchBars = metrics.branches.slice(0, 6).map(branch => {
    const branchInfo = BRANCHES.find(b => b.id === branch.id);
    const label = branchInfo ? (isRTL ? branchInfo.nameAr : branchInfo.name) : branch.id;
    return { id: branch.id, label, value: branch.revenue };
  });

  return (
    <div className="restaurant-analytics-dashboard__section">
      <DashboardSectionHeader
        title={t("restaurantAnalytics.tabs.overview", { defaultValue: "Overview" })}
        subtitle={t("restaurantAnalytics.header.overviewSubtitle", {
          defaultValue: "Restaurant performance across revenue, orders, and demand patterns.",
        })}
        sticky
        actions={<RestaurantHeaderActions {...headerProps} />}
      />

      <RestaurantAiExecutiveSummary tab="overview" metrics={metrics} filters={filters} />

      <div className="restaurant-analytics-dashboard__kpi-row">
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.totalRevenue", { defaultValue: "Total revenue" })}
          value={fmt.formatCurrency(metrics.revenue, { compact: true })}
          delta={metrics.revenueDelta}
          accent="primary"
          tooltip={t("restaurantAnalytics.kpiTooltips.totalRevenue", { defaultValue: "Total revenue across all orders in the selected date range and filters." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.totalOrders", { defaultValue: "Total orders" })}
          value={fmt.formatNumber(metrics.orders)}
          delta={metrics.ordersDelta}
          accent="success"
          tooltip={t("restaurantAnalytics.kpiTooltips.totalOrders", { defaultValue: "Total number of orders placed in the selected date range and filters." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.avgOrderValue", { defaultValue: "Average order value" })}
          value={fmt.formatCurrencyAverage(metrics.aov)}
          delta={metrics.aovDelta}
          accent="warning"
          tooltip={t("restaurantAnalytics.kpiTooltips.avgOrderValue", { defaultValue: "Total revenue divided by total orders." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.uniqueCustomers", { defaultValue: "Unique customers" })}
          value={fmt.formatNumber(metrics.customers)}
          delta={metrics.customersDelta}
          accent="primary"
          tooltipAlign="end"
          tooltip={t("restaurantAnalytics.kpiTooltips.uniqueCustomers", { defaultValue: "Number of distinct customers who placed at least one order." })}
        />
      </div>

      <RestaurantRevenueOrdersTrend metrics={metrics} />

      <div className="restaurant-analytics-dashboard__grid restaurant-analytics-dashboard__grid--two">
        <RestaurantDonutBreakdown
          title={t("restaurantAnalytics.charts.ordersByType", { defaultValue: "Orders by type" })}
          tip={t("restaurantAnalytics.chartTips.ordersByType", { defaultValue: "How order volume splits across dine-in, delivery, takeaway, drive-thru, and pickup." })}
          data={orderTypeDonutData}
          metric="orders"
        />
        <RestaurantCategoryBars
          title={t("restaurantAnalytics.charts.revenueByDaypart", { defaultValue: "Revenue by daypart" })}
          tip={t("restaurantAnalytics.chartTips.revenueByDaypart", { defaultValue: "Revenue split across breakfast, lunch, afternoon, dinner, and late night windows." })}
          data={daypartRevenueBars}
          valueKind="currency"
          colorIndex={1}
        />
      </div>

      <div className="restaurant-analytics-dashboard__grid restaurant-analytics-dashboard__grid--two">
        <RestaurantOrderDensityHeatmap metrics={metrics} />
        <RestaurantHorizontalBars
          title={t("restaurantAnalytics.charts.topBranchesByRevenue", { defaultValue: "Top branches by revenue" })}
          tip={t("restaurantAnalytics.chartTips.topBranchesByRevenue", { defaultValue: "The six highest-earning branches for the selected period and filters." })}
          data={topBranchBars}
          valueKind="currency"
        />
      </div>

      <div className="restaurant-analytics-dashboard__kpi-row">
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.repeatOrderRate", { defaultValue: "Repeat order rate" })}
          value={fmt.formatPercent(metrics.repeatRate)}
          delta={metrics.repeatRateDelta}
          accent="primary"
          tooltip={t("restaurantAnalytics.kpiTooltips.repeatOrderRate", { defaultValue: "Share of customers who placed more than one order in the period." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.itemsPerOrder", { defaultValue: "Avg. items per order" })}
          value={fmt.formatNumber(metrics.itemsPerOrder, 1)}
          delta={metrics.itemsPerOrderDelta}
          accent="success"
          tooltip={t("restaurantAnalytics.kpiTooltips.itemsPerOrder", { defaultValue: "Average number of menu items included per order." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.avgFulfilmentTime", { defaultValue: "Avg. fulfilment time" })}
          value={fmt.formatMinutes(metrics.fulfilmentMinutes)}
          delta={metrics.fulfilmentMinutesDelta}
          accent="warning"
          tooltipAlign="end"
          tooltip={t("restaurantAnalytics.kpiTooltips.avgFulfilmentTime", { defaultValue: "Average time from order placed to order fulfilled." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.cancelledOrders", { defaultValue: "Cancelled orders" })}
          value={fmt.formatNumber(metrics.cancelledOrders)}
          delta={metrics.cancelledOrdersDelta}
          accent="destructive"
          tooltipAlign="end"
          tooltip={t("restaurantAnalytics.kpiTooltips.cancelledOrders", { defaultValue: "Number of orders cancelled before fulfilment." })}
        />
      </div>
    </div>
  );
};

export default RestaurantOverviewTab;
